import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { NoticeFeed } from "../../components/game/Notices.js";
import type { GameLink, GameView } from "../../services/gameLink.js";
import { LeaveParty } from "./LeaveParty.js";

/**
 * Everything that is *available* rather than *asked* — phase 18, finding #12.
 *
 * # Why this is a separate surface at all
 *
 * The old round screen was one scrolling column holding the header, the whole
 * board, the guess controls, the shop, the feed and the exit — so both ends
 * drifted off screen while the least urgent thing owned the middle. What a
 * player needs *constantly* was at the two ends and what they need
 * *occasionally* was in between. Exactly inverted.
 *
 * A stepper fixes that by having one question on screen (see
 * [`RoundStage`](./RoundStage.js)) — which immediately raises the question this
 * file answers: where does the shop go? It cannot be permanently on screen
 * without undoing the stepper, and it cannot be buried, because finding #21 is
 * partly *"a twelve-second window at the foot of a page nobody has scrolled
 * to"*.
 *
 * So: a **drawer on a phone**, opened from the chrome and closed by choosing
 * something; a **second column on a desktop**, where finding #20 measured a
 * thousand empty pixels beside a 768px column that scrolled anyway. Same
 * component, and the caller decides which.
 */
export function RoundExtras({ view, link }: { view: GameView; link: GameLink }) {
  const { t } = useTranslation();
  const board = view.board;
  const you = view.you;
  if (!board) {
    return null;
  }

  return (
    <div data-testid="round-extras" className="flex min-w-0 flex-col gap-5">
      {/* 📌 **The shop and the steal picker are not here any more** — ADR 0026,
          20e-2. Powers are controls on the stage where they work now, from the
          engine's own `available_purchases`; skip is a floating control; and
          challenge is bought by aiming it at a gap. What is left in this column
          is the scrollback and the way out, which is what 20e-4 turns into a
          cog. */}

      {/* The scrollback for what happened while somebody was looking at their
          cards. The *toast* is the one that must not be missed, and phase 18
          moved that out of the flow entirely — see `Notices.tsx`. */}
      <NoticeFeed board={board} roster={view.roster} yourSide={you?.side ?? null} />

      {/* The rules — phase 23. Behind the cog because it is exactly ADR 0026's
          *rare and administrative* row: nobody opens it mid-round, and somebody
          will open it the moment a table disagrees about what a challenge pays.
          ⚠️ A new tab, deliberately: leaving the party to read the rules would
          drop the socket, and the argument this settles is happening now.

          The same page the menu links to outside a party — a rule you can only
          read while playing is a rule you cannot settle an argument with
          afterwards. */}
      <Link
        to="/rules"
        target="_blank"
        rel="noopener"
        data-testid="rules-link"
        className="min-h-12 rounded-lg border border-white/20 px-4 py-3 text-sm font-medium text-white/70"
      >
        {t("rulebook.title")}
      </Link>

      {/* The game's one exit. Phase 16f-1: it shipped in phase 3 and was
          rendered only in the lobby, so from the moment a game started there was
          no way out but the browser's Back button. */}
      <LeaveParty
        isHost={
          view.lobby?.members.find((member) => member.profile_id === link.profileId)
            ?.role === "host"
        }
        inGame={board.phase !== "finished"}
      />
    </div>
  );
}
